<template>
  <div class="w-full mx-auto" :class="store.type === ItemType.VIDEO && !store.isMeeting ? '' : '2xl:max-w-7xl 3xl:max-w-8xl'">
    <router-view />
  </div>
</template>

<script lang="ts" setup>
import { camelCase } from '@replygirl/change-case-object';
import { useCampaignModals } from '@/composables/useCampaignModals';
import { useLayoutStore } from '@/store/useLayoutStore';
import { useUserStore } from '@/store/useUserStore';
import { useCampaignModalsStore, CampaignModalTarget } from '@/store/useCampaignModalsStore';
import { useAiMetadataStore, AiMetadataStatus } from './store/useAiMetadataStore';
import { useAiPlaybackStore } from './store/useAiPlaybackStore';
import { useArtifactsStore } from './store/useArtifactsStore';
import { useRedactionStore } from './store/useRedactionStore';
import { useSmartActionsStore, AiSmartActionStatus } from './store/useSmartActionsStore';
import { useToDoStore } from './store/useToDoStore';
import { useTranscriptionStore, ItemTranscriptionStatus } from './store/useTranscriptionStore';
import { TRANSLATION_FEATURE_FLAG, useTranslationStore } from './store/useTranslationStore';
import { ItemType, useViewerItemStore } from './store/useViewerItemStore';
import { useLogsStore } from './store/useLogsStore';
import { useViewerModals } from '@/modules/viewer/composables/useViewerModals';
import { watch, onMounted, onUnmounted, inject, onBeforeMount } from 'vue';
import { storeToRefs } from 'pinia';
import { PusherPlugin } from '@/plugins/pusher';
import { useCollectionsStore } from '@/store/useCollectionsStore';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';
import { useFirstItemModal } from './composables/useFirstItemModal';
import { useCallbacksStorage } from './composables/useCallbacksStorage';
import { useTranscriptContent } from './composables/useTranscriptContent';
import { useFeatures } from '@/composables/useFeatures';
import { useToDo } from './composables/useToDo';

const campaignModals = useCampaignModals();
const aiMetadataStore = useAiMetadataStore();
const artifactsStore = useArtifactsStore();
const smartActionsStore = useSmartActionsStore();
const toDoStore = useToDoStore();
const { isToDoAvailable } = useToDo();
const transcriptionStore = useTranscriptionStore();
const translationStore = useTranslationStore();
const store = useViewerItemStore();
const viewerModals = useViewerModals();
const layoutStore = useLayoutStore();
const userStore = useUserStore();
const campaignModalsStore = useCampaignModalsStore();
const collectionsStore = useCollectionsStore();
const entitlementsStore = useEntitlementsStore();

const pusher = inject<PusherPlugin>('$pusher');
const CHANNEL_ITEM_UPDATE_EVENT_NAME = 'item_update';
const CHANNEL_CAPTION_FILE_GENERATED = 'caption_file_generated';
const CHANNEL_TRANSCRIPTION_UPDATED_EVENT_NAME = 'transcription_updated';
const CHANNEL_AI_METADATA_UPDATE_EVENT_NAME = 'ai_metadata_update';
const CHANNEL_AI_SMART_ACTION_UPDATE_EVENT_NAME = 'ai_smart_action_update';
const CHANNEL_ENTITLEMENT_QUOTA_UPDATED_EVENT_NAME = 'entitlement_quota_updated';
const CHANNEL_OCR_RESULT_UPDATE_EVENT_NAME = 'ocr_result_update';
const CHANNEL_TO_DO_ITEM_UPDATE_EVENT_NAME = 'to_do_item_update';
const CHANNEL_TO_DO_GENERATION_UPDATE_EVENT_NAME = 'to_do_generation_update';
const CHANNEL_ITEM_TRANSLATION_UPDATE_EVENT_NAME = 'item_translation_update';
const CHANNEL_ITEM_TRANSLATION_CAPTION_GENERATED_EVENT_NAME = 'item_translation_caption_generated';
const CHANNEL_ITEM_TRANSLATION_REMOVED_EVENT_NAME = 'item_translation_removed';
const CHANNEL_REDACTION_UPDATE_EVENT_NAME = 'redaction_update';
// A v5 pusher payload is keyed on the name of the class that broadcast it, and a
// run broadcasts itself.
const REDACTION_PAYLOAD_KEY = 'enrichments::redaction';

const { id, type } = storeToRefs(store);
const translationEnabled = useFeatures().getItemFeature(TRANSLATION_FEATURE_FLAG, false);
const { itemCount } = storeToRefs(userStore);

const isOcrCapableItem = () => store.type === ItemType.SCREENSHOT || store.type === ItemType.ANNOTATED_SCREENSHOT;

const initializePusher = (id?: string) => {
  if (!id || !pusher) return;

  pusher.subscribeToItem(id);
  bindItemPusherEvents();
  bindUserPusherEvents();
};
const loadCampaignModals = () => userStore.isLoggedIn && campaignModalsStore.load(CampaignModalTarget.VIEWER);
const runItemCallbacks = (id?: string) => {
  if (!id) {
    return;
  }
  useCallbacksStorage().run(id);
};

const onQuotaUpdated = (payload: any) => {
  const data = camelCase(payload?.data);
  const attrs = data?.usagequota?.attributes;
  if (attrs?.entitlementType === 'smart_actions') {
    entitlementsStore.$patch(state => {
      state.quotas.smartActions = {
        used: attrs.quota.used,
        remaining: attrs.quota.remaining,
        expiresAt: attrs.quota.expiresAt,
      };
    });
  }
};

const loadFirstItemModal = () => {
  if (itemCount && itemCount.value === 1) {
    const { open: openFirstItemModal } = useFirstItemModal();
    openFirstItemModal();
  }
};

// @todo check whether the item id can still change after mount; if it can't,
// this watcher and every reset in it can be dropped.
watch(id, itemId => {
  initializePusher(itemId);
  itemId && isOcrCapableItem() && artifactsStore.loadArtifacts(itemId);
  smartActionsStore.resetForNewItem();
  // The To-Do count sits in the always-visible tab label, so clear the old list
  // right away rather than waiting for the new request.
  toDoStore.resetForNewItem();
  itemId && toDoStore.fetchToDoItems(itemId);
  runItemCallbacks(itemId);
  // Drop cached logs from the previous item — the new item has its own
  // insights.zip URL (or none). LogsSidePanel will re-fetch on next mount.
  useLogsStore().reset();
  // Live skip counters are session state — this component (the viewer route
  // root) stays mounted across item navigation regardless of whether the AI
  // Actions panel does, so it's the one place a counter reset is guaranteed
  // to fire.
  useAiPlaybackStore().resetForNewItem();
  // The toggles/detected ranges/status now live on aiMetadataStore, and
  // useAiPlaybackEffect (the seek watcher) stays mounted in Video.vue across
  // navigation too — without clearing this, a video with Skip Filler Words
  // on would keep seeking against the previous item's ranges. Reset
  // synchronously, then refetch so whichever item is now current gets its
  // own values back without waiting on a Pusher event.
  aiMetadataStore.resetForNewItem();
  if (itemId) aiMetadataStore.fetchFullMetadata();
  // Same reason as the counters above: if the panel (and useAiPlayback's
  // watcher) was unmounted, this stale value would otherwise survive into
  // the next item and useVideoJs's immediate watch would forward it to the
  // control bar before anything recomputes it.
  store.setAiPlaybackEffectiveRemainingSeconds(null);
  // Nothing else resets this on navigation, and useVideoJs's onDurationChange
  // only overwrites it once the new video's own duration is known. Until
  // then it would otherwise still hold the previous item's runtime, showing
  // a wrong effectiveDurationLabel if a toggle is flipped in that window —
  // and ensureDurationKnown's `if (videoDuration.value) return` would skip
  // the proactive fetch meant to correct it, since the stale value is truthy.
  store.setVideoDuration(0);
});

// Not driven from TranslationSelector: it is remounted whenever the layout
// switches, and initialize() resets the store, which would drop the selected
// language and snapshot the translated text as the original.
watch(
  [id, translationEnabled, type],
  ([itemId, enabled, itemType]) => {
    if (itemId && enabled && itemType === ItemType.VIDEO) {
      translationStore.initialize(itemId);
      return;
    }

    // Otherwise the previous item's translation stays active, and an edit here
    // would be PATCHed onto that item's translation row.
    translationStore.$reset();
  },
  { immediate: true }
);

onUnmounted(() => {
  layoutStore.hideCta();
  layoutStore.showBackButton();

  if (!pusher) return;
  pusher.unbindAllItemChannel();
  pusher.unsubscribeFromItem();
  pusher.unbindUser(CHANNEL_ENTITLEMENT_QUOTA_UPDATED_EVENT_NAME, onQuotaUpdated);
});

onBeforeMount(async () => {
  layoutStore.showCta();
  layoutStore.hideBackButton();
  collectionsStore.load();

  initializePusher(id.value);
  id.value && isOcrCapableItem() && artifactsStore.loadArtifacts(id.value);
  id.value && isToDoAvailable.value && toDoStore.fetchToDoItems(id.value);

  /**
   * @todo remove after resolving [SW-3059] issue
   */
  store.preloadPendingItem();
});

onMounted(() => {
  loadCampaignModals();
  loadFirstItemModal();

  // show modals based on URL query params
  viewerModals.openModalFromUrlQuery();

  // track custom domains view
  store.trackCustomDomainView();

  // run stored callbacks on init
  runItemCallbacks(id.value);
});

const bindItemPusherEvents = () => {
  if (!pusher) return;
  pusher.unbindAllItemChannel();

  pusher.bindItem(CHANNEL_ITEM_UPDATE_EVENT_NAME, onItemMessage);
  pusher.bindItem(CHANNEL_CAPTION_FILE_GENERATED, onItemMessage);
  pusher.bindItem(CHANNEL_REDACTION_UPDATE_EVENT_NAME, onRedactionMessage);
  pusher.bindItem(CHANNEL_AI_METADATA_UPDATE_EVENT_NAME, (payload: any) => {
    const data = camelCase(payload?.data).itemaimetadata;
    const justFinishedProcessing = aiMetadataStore.status !== AiMetadataStatus.DONE && data?.attributes?.status === AiMetadataStatus.DONE;
    aiMetadataStore.patchAIMetaData(data);
    // Only refetch on the pending/working → done transition — an
    // intermediate pending/working event's full resource still has null
    // filler_words/silences (patching that in would briefly clobber values
    // a prior done-fetch already populated), and every later event while
    // already done (e.g. the owner's own skip-toggle save echoing back)
    // already carries everything this store needs in the lightweight
    // payload — filler_words/silences just don't change after done.
    if (justFinishedProcessing) {
      aiMetadataStore.fetchFullMetadata();
    }
  });
  pusher.bindItem(CHANNEL_OCR_RESULT_UPDATE_EVENT_NAME, async (payload: any) => {
    const artifactData = camelCase(payload?.data)['artifactsOcr'];
    if (artifactData?.attributes?.content) {
      artifactsStore.upsertArtifact({
        id: artifactData.id,
        type: artifactData.attributes?.type,
        status: artifactData.attributes?.status,
        createdAt: artifactData.attributes?.createdAt,
        updatedAt: artifactData.attributes?.updatedAt,
        content: artifactData.attributes?.content,
        publicBucketUrls: artifactData.attributes?.publicBucketUrls || [],
        itemId: artifactData.relationships?.item?.data?.id || '',
      });
    } else if (artifactData?.id && id.value) {
      await artifactsStore.loadArtifact(id.value, artifactData.id);
    }
  });
  pusher.bindItem(CHANNEL_AI_SMART_ACTION_UPDATE_EVENT_NAME, (payload: any) => {
    const data = camelCase(payload?.data).aismartaction;
    const justFinished = smartActionsStore.actionStatus !== AiSmartActionStatus.DONE && data?.attributes?.actionStatus === AiSmartActionStatus.DONE;
    smartActionsStore.patchSmartActionsData(data);

    // The server queues the to-do run before marking the action done.
    if (justFinished && isToDoAvailable.value && id.value) {
      toDoStore.fetchToDoItems(id.value);
    }
  });
  // Pusher events arrive in any order, so refetch the server's state instead of
  // applying the payload's status.
  pusher.bindItem(CHANNEL_TO_DO_GENERATION_UPDATE_EVENT_NAME, () => {
    if (id.value) {
      toDoStore.fetchToDoItems(id.value);
    }
  });
  pusher.bindItem(CHANNEL_TO_DO_ITEM_UPDATE_EVENT_NAME, (payload: any) => {
    const data = camelCase(payload?.data).itemtodo;
    toDoStore.patchToDoItem(data);
  });
  // Status-only payloads (Pusheable keys the model as `itemtranslation`); the store refetches content.
  pusher.bindItem(CHANNEL_ITEM_TRANSLATION_UPDATE_EVENT_NAME, (payload: any) => {
    translationStore.onStatusEvent(camelCase(payload?.data)?.itemtranslation?.attributes);
  });
  pusher.bindItem(CHANNEL_ITEM_TRANSLATION_CAPTION_GENERATED_EVENT_NAME, (payload: any) => {
    translationStore.onCaptionGenerated(camelCase(payload?.data)?.itemtranslation?.attributes);
  });
  // The job destroys a pending row when the item is already in that language.
  pusher.bindItem(CHANNEL_ITEM_TRANSLATION_REMOVED_EVENT_NAME, (payload: any) => {
    translationStore.onRemoved(camelCase(payload?.data)?.itemtranslation?.attributes);
  });
  pusher.bindItem(CHANNEL_TRANSCRIPTION_UPDATED_EVENT_NAME, (payload: any) => {
    const status = payload?.data?.transcription?.attributes.status;

    // we don't have full transcription data coming from pusher so we need to make request for them - see serializers
    if (status === ItemTranscriptionStatus.DONE) {
      useTranscriptContent().load(transcriptionStore.transcriptionId);
    } else {
      transcriptionStore.populateTranscriptionData(payload?.data);
    }
  });
};

const bindUserPusherEvents = () => {
  if (!pusher) return;

  pusher.bindUser(CHANNEL_ENTITLEMENT_QUOTA_UPDATED_EVENT_NAME, onQuotaUpdated);
};

const onRedactionMessage = (payload: any) => {
  const run = payload?.data?.[REDACTION_PAYLOAD_KEY];
  if (!run?.attributes) {
    return;
  }

  useRedactionStore().patch(camelCase(run.attributes));
};

// fot now we leave it here - but we need to move it to store or composable
const onItemMessage = (payload: any) => {
  if (!payload || !payload.data || !payload.data.item) {
    return;
  }

  const store = useViewerItemStore();
  store.onPusherUpdate(camelCase(payload.data).item);
};

watch(
  () => userStore.isLoggedIn,
  () => loadCampaignModals()
);

watch(
  () => campaignModalsStore.showCampaignModal,
  () => {
    if (campaignModalsStore.showCampaignModal) {
      return campaignModals.openCampaignModal();
    }
  }
);
</script>
